import { type FC, useState } from "react";
import { useSelector } from "react-redux";
import { Bar, BarChart, CartesianGrid, Cell, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { TriggerButton } from "widgets";

import { type IOperationTrace } from "../../redux/modules/tracing.js";
import { type IAppState } from "../../redux/reducer.js";
import styleLayout from "../core/styles/PageLayout.module.scss";

export const BAR_HEIGHT = 30;

const TracingPage: FC = () => {
  const [selectedOperationIndex, setSelectedOperationIndex] = useState(0);
  const operationTraces = useSelector<IAppState, IOperationTrace[]>((state) => state.tracing.operationTraces);

  if (!operationTraces) {
    return null;
  }

  const operationTrace = operationTraces[selectedOperationIndex];

  return (
    <div className={styleLayout.page}>
      {!operationTrace ? (
        "Waiting for graphQL requests to return."
      ) : (
        <div className="fill" style={{ paddingLeft: 400, width: "100%", position: "absolute", overflow: "auto" }}>
          <h4 style={{ textAlign: "center" }}>
            <TriggerButton
              icon={"arrow_circle_left"}
              label={"Previous"}
              onPress={() => {
                setSelectedOperationIndex((prev) => (prev === 0 ? operationTraces.length - 1 : prev - 1));
              }}
            />
            &nbsp;operation [{operationTrace.operationName}]&nbsp;
            <TriggerButton
              icon={"arrow_circle_left"}
              label={"Next"}
              onPress={() => {
                setSelectedOperationIndex((prev) => (prev === operationTraces.length - 1 ? 0 : prev + 1));
              }}
            />
          </h4>
          <p style={{ textAlign: "center" }}>All traces &lt;1ms are hidden </p>
          <ResponsiveContainer width="100%" height={operationTrace.traces.length * BAR_HEIGHT + 120}>
            <BarChart
              layout="vertical"
              data={operationTrace.traces}
              margin={{ top: 30, right: 30, left: 20, bottom: 30 }}
            >
              <XAxis type="number" orientation="top" />
              <YAxis dataKey="path" type="category" width={250} orientation="right" />
              <CartesianGrid strokeDasharray="3 3" />
              <Tooltip />
              <Legend verticalAlign="top" />
              <Bar dataKey="duration" fill="#84b8d7" barSize={15} unit="ms" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="fill" style={{ width: 400, position: "absolute", overflow: "auto" }}>
        <h4 style={{ textAlign: "center" }}>All graphQL operations</h4>
        <p style={{ textAlign: "center" }}>Click on a bar to display the corresponding field-level traces.</p>
        <ResponsiveContainer width="100%" height={operationTraces.length * BAR_HEIGHT + 120}>
          <BarChart layout="vertical" data={operationTraces} margin={{ top: 30, right: 30, left: 20, bottom: 30 }}>
            <XAxis type="number" orientation="top" />
            <YAxis dataKey="operationName" type="category" width={150} />
            <CartesianGrid strokeDasharray="3 3" />
            <Tooltip />
            <Legend verticalAlign="top" />
            <Bar
              dataKey="duration"
              barSize={15}
              onMouseDown={(_, idx) => {
                setSelectedOperationIndex(idx);
              }}
              unit="ms"
            >
              {operationTraces.map((_, index) => (
                <Cell
                  cursor="pointer"
                  fill={index === selectedOperationIndex ? "#d53e4f" : "#84b8d7"}
                  key={`cell-${index}`}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default TracingPage;
